import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./AdminShared.css";

import { API_BASE_URL } from "../config";

export default function AdminQuizScores() {
  const navigate = useNavigate();
  const [quizScores, setQuizScores] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchQuizScores = useCallback(async () => {
    try {
      setLoading(true);
      const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "null");
      const token = adminInfo?.token;

      if (!token) {
        navigate("/admin");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/quiz/scores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Synchronization failed");
      const data = await res.json();
      setQuizScores(
        Array.isArray(data)
          ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          : [],
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchQuizScores();
  }, [fetchQuizScores]);

  const handleDelete = async (id) => {
    if (!window.confirm("Purge performance record?")) return;
    try {
      const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
      const res = await fetch(`${API_BASE_URL}/quiz/scores/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchQuizScores();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (score) => {
    setEditData({ ...score });
    setShowEditPopup(true);
  };

  const updateQuizScore = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
      const res = await fetch(
        `${API_BASE_URL}/quiz/scores/update/${editData._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editData),
        },
      );
      if (!res.ok) throw new Error("Update failed");
      alert("✅ Score Matrix Updated.");
      setShowEditPopup(false);
      fetchQuizScores();
    } catch (err) {
      alert(err.message);
    }
  };

  const exportToExcel = () => {
    if (quizScores.length === 0) return alert("Buffer empty.");
    const worksheetData = quizScores.map((score, i) => ({
      "S.No": i + 1,
      Name: score.name,
      Email: score.email,
      Phone: score.phone,
      Department: score.department,
      Year: score.year,
      College: score.college,
      "Total Score": score.score,
      "Max Score": score.maxScore,
      Attempts: score.attempts,
      "Temporal Node": new Date(score.createdAt).toLocaleString("en-GB"),
    }));
    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Assessment Analytics");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer]), "FlyTutor_Assessment_Matrix.xlsx");
  };

  if (loading)
    return (
      <div className="admin-list-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "80vh",
            gap: "20px",
          }}
        >
          <div className="loading-spinner"></div>
          <p style={{ fontWeight: 700, color: "#475569" }}>
            ⏳ Synchronizing Assessment Matrix...
          </p>
        </div>
      </div>
    );

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Quiz Analytics</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="module-badge"
            style={{ cursor: "pointer", background: "#22c55e", border: "none" }}
            onClick={exportToExcel}
          >
            Export Analytics 📊
          </button>
          <button
            className="module-badge"
            style={{ cursor: "pointer", background: "#d946ef", border: "none" }}
            onClick={fetchQuizScores}
          >
            Sync Matrix 🔄
          </button>
        </div>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: "#d946ef" }}></div>
            Operator: Assessment Logic Node
          </div>
          <div
            className="admin-status-badge"
            style={{
              background: "#fdf4ff",
              color: "#86198f",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            {quizScores.length} TOTAL ASSESSMENTS
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="elite-table">
            <thead>
              <tr>
                <th>Candidate Node</th>
                <th>Academic Vector</th>
                <th>Performance Index</th>
                <th>Attempts</th>
                <th>Temporal Node</th>
                <th>Operations</th>
              </tr>
            </thead>
            <tbody>
              {quizScores.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: "center",
                      padding: "100px",
                      color: "#94a3b8",
                    }}
                  >
                    No assessment metrics found in current buffer.
                  </td>
                </tr>
              ) : (
                quizScores.map((score, index) => (
                  <tr key={score._id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{score.name}</div>
                      <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {score.email}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {score.phone}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{score.department}</div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {score.college} • Year {score.year}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: 800,
                          color: "#d946ef",
                        }}
                      >
                        {score.score}{" "}
                        <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                          / {score.maxScore}
                        </span>
                      </div>
                      <div style={{ fontSize: "0.7rem", fontWeight: 700 }}>
                        {((score.score / score.maxScore) * 100).toFixed(1)}%
                        RATIO
                      </div>
                    </td>
                    <td>
                      <div
                        className="module-badge"
                        style={{
                          background: "#f5f3ff",
                          color: "#5b21b6",
                          margin: 0,
                        }}
                      >
                        {score.attempts} TRAVERSALS
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {new Date(score.createdAt).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                        {new Date(score.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="action-btn-elite btn-update"
                          onClick={() => handleEdit(score)}
                        >
                          <span>✏️</span>
                        </button>
                        <button
                          className="action-btn-elite btn-delete"
                          onClick={() => handleDelete(score._id)}
                        >
                          <span>🗑️</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showEditPopup && editData && (
        <div
          className="admin-form-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "flex-end",
            zIndex: 1000,
          }}
          onClick={() => setShowEditPopup(false)}
        >
          <div
            className="admin-side-panel"
            style={{
              width: "450px",
              height: "100%",
              background: "#fff",
              boxShadow: "-20px 0 50px rgba(0,0,0,0.1)",
              padding: "40px",
              display: "flex",
              flexDirection: "column",
              gap: "25px",
              animation: "slideIn 0.4s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontWeight: 800, color: "#0f172a" }}>
              Override Assessment
            </h2>

            <div
              className="admin-form-elite"
              style={{ padding: 0, border: "none", boxShadow: "none" }}
            >
              <label>
                Candidate Name{" "}
                <input
                  value={editData.name}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                />
              </label>
              <label>
                Mail Identity{" "}
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) =>
                    setEditData({ ...editData, email: e.target.value })
                  }
                />
              </label>
              <label>
                Communication Node{" "}
                <input
                  value={editData.phone}
                  onChange={(e) =>
                    setEditData({ ...editData, phone: e.target.value })
                  }
                />
              </label>
              <label>
                Department Vector{" "}
                <input
                  value={editData.department}
                  onChange={(e) =>
                    setEditData({ ...editData, department: e.target.value })
                  }
                />
              </label>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <label>
                  Academic Year{" "}
                  <input
                    type="number"
                    value={editData.year}
                    onChange={(e) =>
                      setEditData({ ...editData, year: e.target.value })
                    }
                  />
                </label>
                <label>
                  Attempts{" "}
                  <input
                    type="number"
                    value={editData.attempts}
                    onChange={(e) =>
                      setEditData({ ...editData, attempts: e.target.value })
                    }
                  />
                </label>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                  background: "#fdf4ff",
                  padding: "15px",
                  borderRadius: "12px",
                }}
              >
                <label>
                  Current Score{" "}
                  <input
                    type="number"
                    value={editData.score}
                    onChange={(e) =>
                      setEditData({ ...editData, score: e.target.value })
                    }
                  />
                </label>
                <label>
                  Maximum Index{" "}
                  <input
                    type="number"
                    value={editData.maxScore}
                    onChange={(e) =>
                      setEditData({ ...editData, maxScore: e.target.value })
                    }
                  />
                </label>
              </div>

              <label>
                Campus Node{" "}
                <textarea
                  value={editData.college}
                  onChange={(e) =>
                    setEditData({ ...editData, college: e.target.value })
                  }
                  style={{ height: "80px" }}
                />
              </label>
            </div>

            <div style={{ marginTop: "auto", display: "flex", gap: "10px" }}>
              <button
                className="btn-primary-elite"
                onClick={updateQuizScore}
                style={{ flex: 1, background: "#d946ef" }}
              >
                Update Matrix
              </button>
              <button
                className="btn-update"
                onClick={() => setShowEditPopup(false)}
                style={{ flex: 1, border: "none", background: "#f1f5f9" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
